import React, { useState } from 'react';
import { X, Briefcase, User, ArrowRight, Mail, Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

/**
 * Two-path OTP sign-in modal. Path is chosen first (partner vs customer),
 * then the same OTP flow runs — role is passed to the backend via role_hint.
 */
export default function SignInModal({ open, onClose, initialRole = null, title = 'Sign in' }) {
  const { requestCode, verifyCode } = useAuth();
  const [role, setRole] = useState(initialRole);   // null | 'partner' | 'customer'
  const [step, setStep] = useState('choose');       // 'choose' | 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const reset = () => {
    setRole(initialRole); setStep(initialRole ? 'email' : 'choose');
    setEmail(''); setCode(''); setBusy(false);
  };

  const close = () => { reset(); onClose?.(); };

  const chooseRole = (r) => { setRole(r); setStep('email'); };

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    if (!email.includes('@')) { toast.error('Enter a valid email'); return; }
    setBusy(true);
    try {
      await requestCode(email, role);
      toast.success('Code sent — check your email.');
      setStep('code');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send code');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e?.preventDefault?.();
    if (code.length < 4) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      await verifyCode(email, code.trim());
      toast.success('You’re in.');
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-modal-title"
      data-testid="signin-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md border-2 border-[#E63946] bg-[#0A0A0A] shadow-[0_0_40px_-8px_rgba(230,57,70,0.6)]"
      >
        <button
          onClick={close}
          data-testid="signin-modal-close"
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-white/60 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 md:p-8">
          <div className="mi-eyebrow text-[#E63946] text-[10px] mb-2">// {title.toUpperCase()}</div>
          <h3 id="signin-modal-title" className="mi-headline text-3xl mb-6 leading-tight">
            {step === 'choose' && <>Sign in to<br /><span className="text-[#E63946]">unlock the Wrapulator</span></>}
            {step === 'email' && <>Enter your email</>}
            {step === 'code'  && <>Enter your code</>}
          </h3>

          {step === 'choose' && (
            <div className="space-y-3">
              <button
                onClick={() => chooseRole('partner')}
                data-testid="signin-choose-partner"
                className="w-full text-left border-2 mi-line-strong bg-black/40 p-4 hover:border-[#E63946] transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 border mi-line flex items-center justify-center text-[#E63946] flex-shrink-0">
                    <Briefcase className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="mi-headline text-lg leading-tight">I'm a shop, installer or fleet manager</div>
                    <div className="text-white/60 text-xs mt-1">Wholesale rates · Rate sheet · B2B dashboard</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#E63946] group-hover:translate-x-1 transition-all" />
                </div>
              </button>
              <button
                onClick={() => chooseRole('customer')}
                data-testid="signin-choose-customer"
                className="w-full text-left border-2 mi-line-strong bg-black/40 p-4 hover:border-[#E63946] transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 border mi-line flex items-center justify-center text-[#E63946] flex-shrink-0">
                    <User className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="mi-headline text-lg leading-tight">I'm a retail customer</div>
                    <div className="text-white/60 text-xs mt-1">Ballpark quote · Save & email · Personal vehicles</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#E63946] group-hover:translate-x-1 transition-all" />
                </div>
              </button>
              <div className="text-center text-white/40 text-xs mt-4">
                No password. We'll email you a 6-digit code.
              </div>
            </div>
          )}

          {step === 'email' && (
            <form onSubmit={submitEmail} className="space-y-4">
              <div className="mi-eyebrow text-white/60 text-[10px] flex items-center gap-1">
                {role === 'partner' ? <><Briefcase className="w-3 h-3" /> PARTNER PATH</> : <><User className="w-3 h-3" /> RETAIL PATH</>}
                <button type="button" onClick={() => setStep('choose')} className="ml-auto text-white/50 hover:text-white text-[10px] uppercase">Change</button>
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="email" autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="signin-email-input"
                  className="w-full bg-black/80 border-2 mi-line-strong text-white pl-10 pr-4 py-3 focus:border-[#E63946] focus:outline-none"
                />
              </div>
              <button type="submit" disabled={busy} data-testid="signin-request-code-btn" className="w-full mi-btn-red justify-center">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Send code
              </button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={submitCode} className="space-y-4">
              <div className="text-white/60 text-xs">
                Sent a 6-digit code to <span className="text-white font-mono">{email}</span>.
              </div>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text" inputMode="numeric" autoFocus maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  data-testid="signin-code-input"
                  className="w-full bg-black/80 border-2 mi-line-strong text-white pl-10 pr-4 py-3 tracking-[0.4em] font-mono focus:border-[#E63946] focus:outline-none"
                />
              </div>
              <button type="submit" disabled={busy} data-testid="signin-verify-code-btn" className="w-full mi-btn-red justify-center">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Verify & sign in
              </button>
              <button type="button" onClick={() => setStep('email')} className="w-full text-center text-white/50 hover:text-white text-xs uppercase tracking-widest py-1">
                Wrong email?
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
